import React, { useEffect, useRef, useState, useCallback } from 'react';
import { css } from 'aphrodite';
import FolderRow from './FolderRow';
import { FolderData } from './types';
import styles from './styles';
import { useRootFolderPagination } from './hooks/useRootFolderPagination';
import MatterFolderRow from './MatterFolderRow';

export type FolderPickerTableProps = {
  rootFolder: FolderData;
  selectedId: number | null;
  onRowClick: (rowData: FolderData) => void;
};

const SCROLL_THRESHOLD = 5;

const FolderPickerTable = ({
  rootFolder,
  selectedId,
  onRowClick,
}: FolderPickerTableProps) => {
  const {
    items,
    hasInitialData,
    hasMoreData,
    isLoading,
    hasLoadingError,
    loadNext,
  } = useRootFolderPagination(rootFolder.id);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isMatterExpanded, setIsMatterExpanded] = useState(true);

  // Load initial items when component mounts
  useEffect(() => {
    if (!hasInitialData) {
      loadNext();
    }
  }, [hasInitialData, hasMoreData, loadNext]);

  // Handle scroll events for pagination
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
      if (!hasMoreData || items.length === 0 || isLoading || hasLoadingError)
        return;

      const { scrollTop, scrollHeight, clientHeight } = tableContainer;
      if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
        loadNext();
      }
    };

    tableContainer.addEventListener('scroll', handleScroll);
    return () => tableContainer.removeEventListener('scroll', handleScroll);
  }, [loadNext, hasMoreData, hasLoadingError, isLoading, items.length]);

  const handleMatterToggle = useCallback(() => {
    setIsMatterExpanded((prev) => !prev);
  }, []);

  return (
    <div
      ref={tableContainerRef}
      className={css(styles.tableContainerWithHeight)}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <MatterFolderRow
            key={rootFolder.id}
            folder={rootFolder}
            selectedId={selectedId}
            onRowClick={onRowClick}
            onExpandToggle={handleMatterToggle}
            isExpanded={isMatterExpanded}
            isLoading={!hasInitialData}
          />

          {isMatterExpanded && (
            <>
              {!hasInitialData && (
                <div className={css(styles.loadingIndicator)}>
                  Loading more folders...
                </div>
              )}

              {hasLoadingError ? (
                <div className={css(styles.loadingIndicator)}>
                  Error loading folders
                </div>
              ) : (
                items.map((childFolder) => (
                  <div key={childFolder.id} data-folder-row>
                    <FolderRow
                      folder={childFolder}
                      depth={childFolder.level}
                      selectedId={selectedId}
                      onRowClick={onRowClick}
                    />
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderPickerTable;
